// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Button, Stack, Text } from '@mantine/core';
import { IconPlus, IconStar } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { FilterBar } from '../../components/FilterBar';
import { useReviews } from '../../hooks/useHr';

interface Review {
  id: string;
  employeeId: string;
  reviewerId: string;
  reviewType: string;
  status: string;
  overallRating: number | null;
  dueDate: string;
}

interface ReviewsResponse {
  reviews: {
    data: Review[];
    total: number;
  };
}

function RatingDisplay({ rating }: { rating: number | null }) {
  if (rating == null) {
    return <Text size="sm" c="dimmed">-</Text>;
  }

  let color = 'gray';
  if (rating >= 4) color = 'green';
  else if (rating >= 3) color = 'yellow';
  else if (rating >= 2) color = 'orange';
  else color = 'red';

  return (
    <Text size="sm" fw={600} c={color} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <IconStar size={14} /> {rating.toFixed(1)}
    </Text>
  );
}

export function ReviewListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const { data: queryData, loading } = useReviews({ page, perPage: pageSize });
  const reviewsData = queryData as ReviewsResponse | undefined;
  const data = reviewsData?.reviews?.data ?? [];
  const total = reviewsData?.reviews?.total ?? 0;

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const formatType = (type: string) =>
    type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const columns: Column<Review>[] = [
    {
      key: 'employeeId',
      label: 'Employee',
      sortable: true,
    },
    {
      key: 'reviewerId',
      label: 'Reviewer',
    },
    {
      key: 'reviewType',
      label: 'Type',
      width: 140,
      render: (row) => formatType(row.reviewType),
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'overallRating',
      label: 'Overall Rating',
      width: 140,
      render: (row) => <RatingDisplay rating={row.overallRating} />,
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      sortable: true,
      width: 120,
      render: (row) => row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '-',
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Performance Reviews"
        subtitle={`${total} reviews`}
        actions={
          <Button
            icon={<IconPlus size={16} />}
            onClick={() => navigate('/admin/workforce/hr/reviews')}
          >
            New Review
          </Button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(1);
        }}
        searchPlaceholder="Search reviews..."
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: [
              { value: 'draft', label: 'Draft' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'submitted', label: 'Submitted' },
              { value: 'completed', label: 'Completed' },
            ],
          },
        ]}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
      />

      <DataTable<Record<string, unknown>>
        columns={columns as unknown as Column<Record<string, unknown>>[]}
        data={data as unknown as Record<string, unknown>[]}
        loading={loading}
        pagination={{
          page,
          pageSize,
          total,
          onPageChange: setPage,
        }}
        onRowClick={(row) => {
          const review = row as unknown as Review;
          navigate(`/hr/reviews/${review.id}`);
        }}
        getRowKey={(row) => row.id as string}
        emptyMessage="No reviews found"
      />
    </Stack>
  );
}

export default ReviewListPage;
